import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, List, Kanban, Calendar, User, Flag, ChevronRight, Trash2, Pencil, Check, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { useClients, useUsers } from '@/hooks/queries';
import { TaskStatus, TaskPriority } from '@taxistry/types';
import type { Task, CreateTaskRequest, UpdateTaskRequest, TaskListParams, PaginatedResponse } from '@taxistry/types';

// ─── Types ───────────────────────────────────────

type TaskRow = Task & {
  clientBusinessName?: string;
  assignedToUserName?: string;
  subtaskCount?: number;
};

type BoardData = {
  todo: TaskRow[];
  inProgress: TaskRow[];
  done: TaskRow[];
};

type BoardColumn = {
  status: TaskStatus;
  label: string;
  tasks: TaskRow[];
};

type ViewMode = 'kanban' | 'list';

// ─── Helper functions ────────────────────────────

function toQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

function statusLabel(status: TaskStatus): string {
  switch (status) {
    case TaskStatus.TODO: return 'Εκκρεμές';
    case TaskStatus.IN_PROGRESS: return 'Σε εξέλιξη';
    case TaskStatus.DONE: return 'Ολοκληρωμένο';
    case TaskStatus.CANCELLED: return 'Ακυρωμένο';
    default: return status;
  }
}

function priorityLabel(priority: TaskPriority): string {
  switch (priority) {
    case TaskPriority.LOW: return 'Χαμηλή';
    case TaskPriority.NORMAL: return 'Κανονική';
    case TaskPriority.HIGH: return 'Υψηλή';
    case TaskPriority.URGENT: return 'Επείγον';
    default: return priority;
  }
}

function priorityColor(priority: TaskPriority): string {
  switch (priority) {
    case TaskPriority.URGENT: return 'status-danger';
    case TaskPriority.HIGH: return 'status-review';
    case TaskPriority.NORMAL: return 'status-new';
    case TaskPriority.LOW: return 'bg-secondary text-muted-foreground';
    default: return 'bg-secondary text-muted-foreground';
  }
}

function statusColor(status: TaskStatus): string {
  switch (status) {
    case TaskStatus.TODO: return 'status-new';
    case TaskStatus.IN_PROGRESS: return 'status-review';
    case TaskStatus.DONE: return 'status-ok';
    case TaskStatus.CANCELLED: return 'status-danger';
    default: return 'bg-secondary text-muted-foreground';
  }
}

// ─── Hooks ───────────────────────────────────────

function useTasks(params: TaskListParams = {}) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => api.get<PaginatedResponse<TaskRow>>(`/tasks?${toQuery(params as Record<string, unknown>)}`),
  });
}

function useTasksBoard() {
  return useQuery({
    queryKey: ['tasks', 'board'],
    queryFn: () => api.get<BoardData>('/tasks/board'),
  });
}

function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskRequest) => api.post<Task>('/tasks', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskRequest }) =>
      api.patch<Task>(`/tasks/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ─── Main Page ───────────────────────────────────

export function TasksPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewMode>('kanban');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskRow | null>(null);

  // List view filters
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data: boardData, isLoading: boardLoading } = useTasksBoard();
  const { data: listData, isLoading: listLoading } = useTasks({
    page,
    limit,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    assignedToUserId: assignedFilter || undefined,
    search: search || undefined,
  });

  const tasks = listData?.data ?? [];
  const total = listData?.total ?? 0;
  const totalPages = listData?.totalPages ?? 1;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-display">Εργασίες</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Διαχείριση εργασιών γραφείου
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-[12px] border border-border">
            <Button
              variant={view === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('kanban')}
              className="rounded-r-none"
            >
              <Kanban className="h-4 w-4 mr-1.5" />
              Kanban
            </Button>
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
              className="rounded-l-none border-l"
            >
              <List className="h-4 w-4 mr-1.5" />
              Λίστα
            </Button>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Νέα Εργασία
          </Button>
        </div>
      </div>

      {/* Kanban View */}
      {view === 'kanban' && (
        <KanbanView
          data={boardData}
          isLoading={boardLoading}
          onEditTask={setEditingTask}
        />
      )}

      {/* List View */}
      {view === 'list' && (
        <ListView
          tasks={tasks}
          total={total}
          page={page}
          limit={limit}
          totalPages={totalPages}
          isLoading={listLoading}
          statusFilter={statusFilter}
          priorityFilter={priorityFilter}
          assignedFilter={assignedFilter}
          search={search}
          onStatusFilterChange={setStatusFilter}
          onPriorityFilterChange={setPriorityFilter}
          onAssignedFilterChange={setAssignedFilter}
          onSearchChange={setSearch}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onEditTask={setEditingTask}
        />
      )}

      {/* Create Dialog */}
      {createDialogOpen && (
        <CreateTaskDialog onClose={() => setCreateDialogOpen(false)} />
      )}

      {/* Edit Dialog */}
      {editingTask && (
        <EditTaskDialog task={editingTask} onClose={() => setEditingTask(null)} />
      )}
    </div>
  );
}

// ─── Kanban View ─────────────────────────────────

function KanbanView({
  data,
  isLoading,
  onEditTask,
}: {
  data: BoardData | undefined;
  isLoading: boolean;
  onEditTask: (task: TaskRow) => void;
}) {
  const updateTask = useUpdateTask();

  const handleMoveTask = (taskId: string, newStatus: TaskStatus) => {
    updateTask.mutate({ id: taskId, data: { status: newStatus } });
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[1, 2, 3].map((j) => <Skeleton key={j} className="h-24" />)}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const columns: BoardColumn[] = [
    { status: TaskStatus.TODO, label: statusLabel(TaskStatus.TODO), tasks: data?.todo ?? [] },
    { status: TaskStatus.IN_PROGRESS, label: statusLabel(TaskStatus.IN_PROGRESS), tasks: data?.inProgress ?? [] },
    { status: TaskStatus.DONE, label: statusLabel(TaskStatus.DONE), tasks: data?.done ?? [] },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {columns.map((column) => (
        <KanbanColumn
          key={column.status}
          column={column}
          onMoveTask={handleMoveTask}
          onEditTask={onEditTask}
        />
      ))}
    </div>
  );
}

// ─── Kanban Column ───────────────────────────────

function KanbanColumn({
  column,
  onMoveTask,
  onEditTask,
}: {
  column: BoardColumn;
  onMoveTask: (taskId: string, newStatus: TaskStatus) => void;
  onEditTask: (task: TaskRow) => void;
}) {
  const getNextStatus = (currentStatus: TaskStatus): TaskStatus | null => {
    if (currentStatus === TaskStatus.TODO) return TaskStatus.IN_PROGRESS;
    if (currentStatus === TaskStatus.IN_PROGRESS) return TaskStatus.DONE;
    return null;
  };

  const nextStatus = getNextStatus(column.status);

  return (
    <Card className="bg-surface-2 border-border rounded-2xl shadow-lifted">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium font-display flex items-center justify-between">
          <span>{column.label}</span>
          <Badge variant="secondary" className="text-xs">
            {column.tasks.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {column.tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Δεν υπάρχουν εργασίες
          </p>
        ) : (
          column.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              nextStatus={nextStatus}
              onMoveTask={onMoveTask}
              onEditTask={onEditTask}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ─── Task Card ───────────────────────────────────

function TaskCard({
  task,
  nextStatus,
  onMoveTask,
  onEditTask,
}: {
  task: TaskRow;
  nextStatus: TaskStatus | null;
  onMoveTask: (taskId: string, newStatus: TaskStatus) => void;
  onEditTask: (task: TaskRow) => void;
}) {
  const deleteTask = useDeleteTask();

  return (
    <Card className="rounded-2xl border-border shadow-lifted hover-lift group">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm flex-1">{task.title}</h3>
          <Badge className={`text-xs rounded-full ${priorityColor(task.priority)}`}>
            {priorityLabel(task.priority)}
          </Badge>
        </div>

        {task.clientBusinessName ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            {task.clientBusinessName}
          </div>
        ) : null}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {task.dueDate ? (
            <div className="flex items-center gap-1.5 font-data">
              <Calendar className="h-3 w-3" />
              {new Date(task.dueDate).toLocaleDateString('el-GR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </div>
          ) : (
            <span>—</span>
          )}
          {task.assignedToUserName ? (
            <span className="text-xs">{task.assignedToUserName}</span>
          ) : null}
        </div>

        <div className="flex gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {nextStatus && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs rounded-[12px]"
              onClick={() => onMoveTask(task.id, nextStatus)}
            >
              <ChevronRight className="h-3 w-3 mr-1" />
              {statusLabel(nextStatus)}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEditTask(task)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              if (confirm('Διαγραφή εργασίας;')) {
                deleteTask.mutate(task.id);
              }
            }}
          >
            <Trash2 className="h-3 w-3 text-red-600" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── List View ───────────────────────────────────

function ListView({
  tasks,
  total,
  page,
  limit,
  totalPages,
  isLoading,
  statusFilter,
  priorityFilter,
  assignedFilter,
  search,
  onStatusFilterChange,
  onPriorityFilterChange,
  onAssignedFilterChange,
  onSearchChange,
  onPageChange,
  onLimitChange,
  onEditTask,
}: {
  tasks: TaskRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  isLoading: boolean;
  statusFilter: TaskStatus | '';
  priorityFilter: TaskPriority | '';
  assignedFilter: string;
  search: string;
  onStatusFilterChange: (value: TaskStatus | '') => void;
  onPriorityFilterChange: (value: TaskPriority | '') => void;
  onAssignedFilterChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onEditTask: (task: TaskRow) => void;
}) {
  const { data: usersData } = useUsers();
  const users = usersData?.data ?? [];
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  return (
    <>
      {/* Filters */}
      <div className="flex items-center gap-3">
        <Input
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
            onPageChange(1);
          }}
          placeholder="Αναζήτηση εργασίας..."
          className="max-w-xs"
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            onStatusFilterChange(v === 'ALL' ? '' : (v as TaskStatus));
            onPageChange(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Κατάσταση" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Όλες</SelectItem>
            <SelectItem value={TaskStatus.TODO}>{statusLabel(TaskStatus.TODO)}</SelectItem>
            <SelectItem value={TaskStatus.IN_PROGRESS}>{statusLabel(TaskStatus.IN_PROGRESS)}</SelectItem>
            <SelectItem value={TaskStatus.DONE}>{statusLabel(TaskStatus.DONE)}</SelectItem>
            <SelectItem value={TaskStatus.CANCELLED}>{statusLabel(TaskStatus.CANCELLED)}</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={priorityFilter}
          onValueChange={(v) => {
            onPriorityFilterChange(v === 'ALL' ? '' : (v as TaskPriority));
            onPageChange(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Προτεραιότητα" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Όλες</SelectItem>
            <SelectItem value={TaskPriority.URGENT}>{priorityLabel(TaskPriority.URGENT)}</SelectItem>
            <SelectItem value={TaskPriority.HIGH}>{priorityLabel(TaskPriority.HIGH)}</SelectItem>
            <SelectItem value={TaskPriority.NORMAL}>{priorityLabel(TaskPriority.NORMAL)}</SelectItem>
            <SelectItem value={TaskPriority.LOW}>{priorityLabel(TaskPriority.LOW)}</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={assignedFilter}
          onValueChange={(v) => {
            onAssignedFilterChange(v === 'ALL' ? '' : v);
            onPageChange(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Ανατέθηκε σε" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Όλοι</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="rounded-2xl border-border shadow-lifted">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : (
          <Table className="text-[13px]">
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] uppercase tracking-wider">Τίτλος</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Πελάτης</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Προτεραιότητα</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Κατάσταση</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Ανατέθηκε σε</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Προθεσμία</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-right">Ενέργειες</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Δεν βρέθηκαν εργασίες
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => (
                  <TableRow key={task.id} className="group table-row-hover">
                    <TableCell className="font-medium">{task.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {task.clientBusinessName ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs rounded-full ${priorityColor(task.priority)}`}>
                        {priorityLabel(task.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs rounded-full ${statusColor(task.status)}`}>
                        {statusLabel(task.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {task.assignedToUserName ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-data">
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString('el-GR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                        {task.status !== TaskStatus.DONE && task.status !== TaskStatus.CANCELLED && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Ολοκλήρωση"
                            onClick={() => updateTask.mutate({ id: task.id, data: { status: TaskStatus.DONE } })}
                          >
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                        )}
                        {task.status === TaskStatus.TODO && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Σε εξέλιξη"
                            onClick={() => updateTask.mutate({ id: task.id, data: { status: TaskStatus.IN_PROGRESS } })}
                          >
                            <ChevronRight className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Επεξεργασία"
                          onClick={() => onEditTask(task)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Διαγραφή"
                          onClick={() => {
                            if (confirm('Διαγραφή εργασίας;')) {
                              deleteTask.mutate(task.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="px-4 py-3 border-t">
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
            />
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Create Task Dialog ──────────────────────────

function CreateTaskDialog({ onClose }: { onClose: () => void }) {
  const createTask = useCreateTask();
  const { data: clientsData } = useClients({ limit: 500 });
  const { data: usersData } = useUsers();

  const clients = clientsData?.data ?? [];
  const users = usersData?.data ?? [];

  const [form, setForm] = useState<CreateTaskRequest>({
    title: '',
    description: '',
    priority: TaskPriority.NORMAL,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createTask.mutateAsync(form);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Νέα Εργασία</DialogTitle>
          <DialogDescription>
            Δημιουργήστε μια νέα εργασία για το γραφείο σας
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Τίτλος *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="π.χ. Υποβολή Φ1 Ιανουαρίου"
              required
            />
          </div>
          <div>
            <Label>Περιγραφή</Label>
            <Textarea
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Προαιρετική περιγραφή..."
              rows={3}
            />
          </div>
          <div>
            <Label>Πελάτης</Label>
            <Select
              value={form.clientId ?? '__none__'}
              onValueChange={(v) => setForm((f) => ({ ...f, clientId: v === '__none__' ? undefined : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε πελάτη (προαιρετικό)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Χωρίς πελάτη</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.businessName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Προτεραιότητα</Label>
              <Select
                value={form.priority ?? TaskPriority.NORMAL}
                onValueChange={(v) => setForm((f) => ({ ...f, priority: v as TaskPriority }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TaskPriority.LOW}>{priorityLabel(TaskPriority.LOW)}</SelectItem>
                  <SelectItem value={TaskPriority.NORMAL}>{priorityLabel(TaskPriority.NORMAL)}</SelectItem>
                  <SelectItem value={TaskPriority.HIGH}>{priorityLabel(TaskPriority.HIGH)}</SelectItem>
                  <SelectItem value={TaskPriority.URGENT}>{priorityLabel(TaskPriority.URGENT)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ανάθεση σε</Label>
              <Select
                value={form.assignedToUserId ?? '__none__'}
                onValueChange={(v) => setForm((f) => ({ ...f, assignedToUserId: v === '__none__' ? undefined : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Κανένας" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Κανένας</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Προθεσμία</Label>
            <Input
              type="date"
              value={form.dueDate ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value || undefined }))}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={createTask.isPending}>
              {createTask.isPending ? 'Δημιουργία...' : 'Δημιουργία'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Task Dialog ────────────────────────────

function EditTaskDialog({ task, onClose }: { task: TaskRow; onClose: () => void }) {
  const updateTask = useUpdateTask();
  const { data: clientsData } = useClients({ limit: 500 });
  const { data: usersData } = useUsers();

  const clients = clientsData?.data ?? [];
  const users = usersData?.data ?? [];

  const [form, setForm] = useState<UpdateTaskRequest>({
    title: task.title,
    description: task.description ?? '',
    status: task.status,
    priority: task.priority,
    assignedToUserId: task.assignedToUserId ?? undefined,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateTask.mutateAsync({ id: task.id, data: form });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Επεξεργασία Εργασίας</DialogTitle>
          <DialogDescription>
            Τροποποιήστε τα στοιχεία της εργασίας
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Τίτλος *</Label>
            <Input
              value={form.title ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          <div>
            <Label>Περιγραφή</Label>
            <Textarea
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Κατάσταση</Label>
              <Select
                value={form.status ?? task.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v as TaskStatus }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TaskStatus.TODO}>{statusLabel(TaskStatus.TODO)}</SelectItem>
                  <SelectItem value={TaskStatus.IN_PROGRESS}>{statusLabel(TaskStatus.IN_PROGRESS)}</SelectItem>
                  <SelectItem value={TaskStatus.DONE}>{statusLabel(TaskStatus.DONE)}</SelectItem>
                  <SelectItem value={TaskStatus.CANCELLED}>{statusLabel(TaskStatus.CANCELLED)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Προτεραιότητα</Label>
              <Select
                value={form.priority ?? task.priority}
                onValueChange={(v) => setForm((f) => ({ ...f, priority: v as TaskPriority }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TaskPriority.LOW}>{priorityLabel(TaskPriority.LOW)}</SelectItem>
                  <SelectItem value={TaskPriority.NORMAL}>{priorityLabel(TaskPriority.NORMAL)}</SelectItem>
                  <SelectItem value={TaskPriority.HIGH}>{priorityLabel(TaskPriority.HIGH)}</SelectItem>
                  <SelectItem value={TaskPriority.URGENT}>{priorityLabel(TaskPriority.URGENT)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Ανάθεση σε</Label>
            <Select
              value={form.assignedToUserId ?? '__none__'}
              onValueChange={(v) => setForm((f) => ({ ...f, assignedToUserId: v === '__none__' ? undefined : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Κανένας" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Κανένας</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Προθεσμία</Label>
            <Input
              type="date"
              value={form.dueDate ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value || undefined }))}
            />
          </div>
          {task.clientBusinessName ? (
            <div className="text-sm text-muted-foreground">
              Πελάτης: <span className="font-medium text-foreground">{task.clientBusinessName}</span>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={updateTask.isPending}>
              {updateTask.isPending ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
